<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import BrandColorField from './BrandColorField.vue';
import LoginFeaturesEditor from './LoginFeaturesEditor.vue';
import BrandingPreview from './BrandingPreview.vue';

const store = useStore();
const { t } = useI18n();

const profile = useMapGetter('reseller/getProfile');

const brandName = ref('');
const brandUrl = ref('');
const brandColor = ref('#1F73B7');
const customDomain = ref('');
const loginBgColor = ref('#1f93ff');
const loginPageTitle = ref('');
const loginPageDescription = ref('');
const loginTitleColor = ref('#0F172A');
const loginDescriptionColor = ref('#475569');
const loginOverlayOpacity = ref(80);
const loginFormPosition = ref('left');
const loginFeatures = ref([]);
const logoFile = ref(null);
const logoDarkFile = ref(null);
const faviconFile = ref(null);
const loginBgImageFile = ref(null);
const isSaving = ref(false);
const isResetting = ref(false);

const logoPreviewUrl = ref(null);
const logoDarkPreviewUrl = ref(null);
const loginBgPreviewUrl = ref(null);

const logoUrl = computed(() => profile.value?.branding?.logo_url || null);
const logoDarkUrl = computed(
  () => profile.value?.branding?.logo_dark_url || null
);
const faviconUrl = computed(() => profile.value?.branding?.favicon_url || null);
const loginBgImageUrl = computed(
  () => profile.value?.branding?.login_bg_image_url || null
);
const domainInfo = computed(() => profile.value?.domain || {});
const cnameRecord = computed(() => {
  const host = customDomain.value || t('REVENDA.BRANDING.DOMAIN.PLACEHOLDER');
  return `${host} CNAME ${domainInfo.value.cname_target || ''}`;
});

const hydrate = () => {
  brandName.value = profile.value?.branding?.brand_name || '';
  brandUrl.value = profile.value?.branding?.brand_url || '';
  brandColor.value = profile.value?.branding?.brand_color || '#1F73B7';
  loginBgColor.value = profile.value?.branding?.login_bg_color || '#1f93ff';
  loginPageTitle.value = profile.value?.branding?.login_page_title || '';
  loginPageDescription.value =
    profile.value?.branding?.login_page_description || '';
  loginTitleColor.value =
    profile.value?.branding?.login_title_color || '#0F172A';
  loginDescriptionColor.value =
    profile.value?.branding?.login_description_color || '#475569';
  loginOverlayOpacity.value =
    profile.value?.branding?.login_overlay_opacity ?? 80;
  loginFormPosition.value =
    profile.value?.branding?.login_form_position || 'left';
  loginFeatures.value = profile.value?.branding?.login_page_features || [];
  customDomain.value = profile.value?.domain?.custom_domain || '';
};

onMounted(() => {
  if (!profile.value) {
    store.dispatch('reseller/getProfile').then(hydrate);
  } else {
    hydrate();
  }
});

const onLogoChange = event => {
  const [file] = event.target.files;
  logoFile.value = file || null;
  if (logoPreviewUrl.value) URL.revokeObjectURL(logoPreviewUrl.value);
  logoPreviewUrl.value = file ? URL.createObjectURL(file) : null;
};

const onLogoDarkChange = event => {
  const [file] = event.target.files;
  logoDarkFile.value = file || null;
  if (logoDarkPreviewUrl.value) URL.revokeObjectURL(logoDarkPreviewUrl.value);
  logoDarkPreviewUrl.value = file ? URL.createObjectURL(file) : null;
};

const onFaviconChange = event => {
  const [file] = event.target.files;
  faviconFile.value = file || null;
};

const onLoginBgImageChange = event => {
  const [file] = event.target.files;
  loginBgImageFile.value = file || null;
  if (loginBgPreviewUrl.value) URL.revokeObjectURL(loginBgPreviewUrl.value);
  loginBgPreviewUrl.value = file ? URL.createObjectURL(file) : null;
};

onBeforeUnmount(() => {
  [logoPreviewUrl, logoDarkPreviewUrl, loginBgPreviewUrl].forEach(r => {
    if (r.value) URL.revokeObjectURL(r.value);
  });
});

const logoDisplayUrl = computed(() => logoPreviewUrl.value || logoUrl.value);
const logoDarkDisplayUrl = computed(
  () => logoDarkPreviewUrl.value || logoDarkUrl.value
);
const loginBgDisplayUrl = computed(
  () => loginBgPreviewUrl.value || loginBgImageUrl.value
);

const previewBranding = computed(() => ({
  brandColor: brandColor.value,
  logoUrl: logoPreviewUrl.value || logoUrl.value,
  logoDarkUrl: logoDarkPreviewUrl.value || logoDarkUrl.value,
  loginBgImageUrl: loginBgPreviewUrl.value || loginBgImageUrl.value,
  loginPageTitle: loginPageTitle.value,
  loginPageDescription: loginPageDescription.value,
  loginTitleColor: loginTitleColor.value,
  loginDescriptionColor: loginDescriptionColor.value,
  loginOverlayColor: loginBgColor.value,
  loginOverlayOpacity: loginOverlayOpacity.value,
  loginFormPosition: loginFormPosition.value,
  loginFeatures: loginFeatures.value,
}));

const onReset = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('REVENDA.BRANDING.RESET_CONFIRM'))) return;
  isResetting.value = true;
  try {
    await store.dispatch('reseller/resetProfile');
    hydrate();
    [logoPreviewUrl, logoDarkPreviewUrl, loginBgPreviewUrl].forEach(r => {
      if (r.value) URL.revokeObjectURL(r.value);
      r.value = null;
    });
    logoFile.value = null;
    logoDarkFile.value = null;
    faviconFile.value = null;
    loginBgImageFile.value = null;
    useAlert(t('REVENDA.BRANDING.RESET_DONE'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('REVENDA.BRANDING.ERROR'));
  } finally {
    isResetting.value = false;
  }
};

const submit = async () => {
  isSaving.value = true;
  try {
    const fd = new FormData();
    fd.append('profile[brand_name]', brandName.value);
    fd.append('profile[brand_url]', brandUrl.value);
    fd.append('profile[brand_color]', brandColor.value);
    fd.append('profile[custom_domain]', customDomain.value);
    fd.append('profile[login_bg_color]', loginBgColor.value);
    fd.append('profile[login_page_title]', loginPageTitle.value);
    fd.append('profile[login_page_description]', loginPageDescription.value);
    fd.append('profile[login_title_color]', loginTitleColor.value);
    fd.append('profile[login_description_color]', loginDescriptionColor.value);
    fd.append(
      'profile[login_overlay_opacity]',
      String(loginOverlayOpacity.value)
    );
    fd.append('profile[login_form_position]', loginFormPosition.value);
    loginFeatures.value.forEach((f, i) => {
      fd.append(`profile[login_page_features][${i}][icon]`, f.icon || '');
      fd.append(`profile[login_page_features][${i}][title]`, f.title || '');
      fd.append(
        `profile[login_page_features][${i}][description]`,
        f.description || ''
      );
    });
    if (logoFile.value) fd.append('profile[logo]', logoFile.value);
    if (logoDarkFile.value) fd.append('profile[logo_dark]', logoDarkFile.value);
    if (faviconFile.value) fd.append('profile[favicon]', faviconFile.value);
    if (loginBgImageFile.value)
      fd.append('profile[login_bg_image]', loginBgImageFile.value);
    await store.dispatch('reseller/updateProfile', fd);
    useAlert(t('REVENDA.BRANDING.SAVED'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('REVENDA.BRANDING.ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col w-full gap-4 py-6">
    <div>
      <h2 class="text-lg font-medium text-n-slate-12">
        {{ t('REVENDA.BRANDING.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ t('REVENDA.BRANDING.SUBTITLE') }}
      </p>
    </div>

    <div class="flex flex-col gap-6 lg:flex-row lg:items-start">
      <div class="flex flex-col flex-1 gap-6">
        <div class="grid gap-6 lg:grid-cols-2">
          <!-- Identidade e cores -->
          <div
            class="flex flex-col gap-4 p-5 border rounded-xl border-n-weak bg-n-solid-2"
          >
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('REVENDA.BRANDING.SECTION_IDENTITY') }}
            </p>

            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{ t('REVENDA.BRANDING.BRAND_NAME') }}
              <input v-model="brandName" type="text" class="w-full" />
            </label>

            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{ t('REVENDA.BRANDING.BRAND_URL') }}
              <input
                v-model="brandUrl"
                type="url"
                :placeholder="t('REVENDA.BRANDING.BRAND_URL_PLACEHOLDER')"
                class="w-full"
              />
              <span class="text-xs text-n-slate-11">
                {{ t('REVENDA.BRANDING.BRAND_URL_HINT') }}
              </span>
            </label>

            <div class="grid grid-cols-2 gap-4">
              <BrandColorField
                v-model="brandColor"
                :label="t('REVENDA.BRANDING.BRAND_COLOR')"
              />
            </div>

            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{ t('REVENDA.BRANDING.DOMAIN.LABEL') }}
              <input
                v-model="customDomain"
                type="text"
                :placeholder="t('REVENDA.BRANDING.DOMAIN.PLACEHOLDER')"
                class="w-full"
              />
              <span class="text-xs text-n-slate-11">
                {{ t('REVENDA.BRANDING.DOMAIN.HINT') }}
              </span>

              <span
                v-if="domainInfo.external"
                class="flex flex-col gap-1 p-3 mt-1 border rounded-lg bg-n-solid-1 border-n-weak"
              >
                <span class="text-xs text-n-slate-11">
                  {{ t('REVENDA.BRANDING.DOMAIN.CNAME_INSTRUCTION') }}
                </span>
                <code class="text-xs break-all text-n-slate-12">{{
                  cnameRecord
                }}</code>
                <span
                  v-if="!domainInfo.saas_enabled"
                  class="mt-1 text-xs text-n-amber-11"
                >
                  {{ t('REVENDA.BRANDING.DOMAIN.NOT_CONFIGURED') }}
                </span>
                <span
                  v-else-if="domainInfo.error"
                  class="mt-1 text-xs text-n-ruby-11"
                >
                  {{ domainInfo.error }}
                </span>
                <span
                  v-else-if="domainInfo.ssl_status === 'active'"
                  class="mt-1 text-xs text-n-teal-11"
                >
                  {{ t('REVENDA.BRANDING.DOMAIN.ACTIVE') }}
                </span>
                <span
                  v-else-if="domainInfo.ssl_status"
                  class="mt-1 text-xs text-n-slate-11"
                >
                  {{ t('REVENDA.BRANDING.DOMAIN.PENDING') }}
                </span>
              </span>
            </label>
          </div>

          <!-- Imagens -->
          <div
            class="flex flex-col gap-4 p-5 border rounded-xl border-n-weak bg-n-solid-2"
          >
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('REVENDA.BRANDING.SECTION_IMAGES') }}
            </p>

            <div class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{ t('REVENDA.BRANDING.LOGO') }}
              <img
                v-if="logoDisplayUrl"
                :src="logoDisplayUrl"
                :alt="t('REVENDA.BRANDING.LOGO')"
                class="object-contain h-12 mb-1"
              />
              <input type="file" accept="image/*" @change="onLogoChange" />
            </div>

            <div class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{ t('REVENDA.BRANDING.LOGO_DARK') }}
              <img
                v-if="logoDarkDisplayUrl"
                :src="logoDarkDisplayUrl"
                :alt="t('REVENDA.BRANDING.LOGO_DARK')"
                class="object-contain h-12 mb-1"
              />
              <input type="file" accept="image/*" @change="onLogoDarkChange" />
            </div>

            <div class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{ t('REVENDA.BRANDING.FAVICON') }}
              <img
                v-if="faviconUrl"
                :src="faviconUrl"
                :alt="t('REVENDA.BRANDING.FAVICON')"
                class="object-contain w-8 h-8 mb-1"
              />
              <input
                type="file"
                accept="image/png,image/x-icon,image/svg+xml"
                @change="onFaviconChange"
              />
              <span class="text-xs text-n-slate-11">
                {{ t('REVENDA.BRANDING.FAVICON_HINT') }}
              </span>
            </div>

            <div class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{ t('REVENDA.BRANDING.LOGIN_BG') }}
              <img
                v-if="loginBgDisplayUrl"
                :src="loginBgDisplayUrl"
                :alt="t('REVENDA.BRANDING.LOGIN_BG')"
                class="object-cover w-32 h-16 mt-1 rounded"
              />
              <input
                type="file"
                accept="image/*"
                @change="onLoginBgImageChange"
              />
              <span class="text-xs text-n-slate-11">
                {{ t('REVENDA.BRANDING.LOGIN_BG_HINT') }}
              </span>
            </div>
          </div>
        </div>

        <!-- Tela de login -->
        <div
          class="flex flex-col gap-4 p-5 border rounded-xl border-n-weak bg-n-solid-2"
        >
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('REVENDA.BRANDING.LOGIN.SECTION') }}
          </p>

          <label class="flex flex-col gap-1 text-sm text-n-slate-12">
            {{ t('REVENDA.BRANDING.LOGIN.PAGE_TITLE') }}
            <input v-model="loginPageTitle" type="text" class="w-full" />
          </label>

          <label class="flex flex-col gap-1 text-sm text-n-slate-12">
            {{ t('REVENDA.BRANDING.LOGIN.PAGE_DESCRIPTION') }}
            <input v-model="loginPageDescription" type="text" class="w-full" />
          </label>

          <div class="grid grid-cols-2 gap-4">
            <BrandColorField
              v-model="loginTitleColor"
              :label="t('REVENDA.BRANDING.LOGIN.TITLE_COLOR')"
            />
            <BrandColorField
              v-model="loginDescriptionColor"
              :label="t('REVENDA.BRANDING.LOGIN.DESCRIPTION_COLOR')"
            />
          </div>

          <div class="grid grid-cols-2 gap-4">
            <BrandColorField
              v-model="loginBgColor"
              :label="t('REVENDA.BRANDING.LOGIN.OVERLAY_COLOR')"
            />
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{ t('REVENDA.BRANDING.LOGIN.OVERLAY_OPACITY') }}
              <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
              <span>{{ loginOverlayOpacity }}%</span>
              <input
                v-model.number="loginOverlayOpacity"
                type="range"
                min="0"
                max="100"
                class="w-full"
              />
            </label>
          </div>

          <div class="flex flex-col gap-1">
            <span class="text-sm text-n-slate-12">
              {{ t('REVENDA.BRANDING.LOGIN.FORM_POSITION') }}
            </span>
            <div class="flex gap-4">
              <label
                v-for="pos in ['left', 'center', 'right']"
                :key="pos"
                class="flex items-center gap-1 text-sm text-n-slate-11"
              >
                <input
                  v-model="loginFormPosition"
                  type="radio"
                  :value="pos"
                  name="login_form_position"
                />
                {{
                  pos === 'left'
                    ? t('REVENDA.BRANDING.LOGIN.POSITION_LEFT')
                    : pos === 'center'
                      ? t('REVENDA.BRANDING.LOGIN.POSITION_CENTER')
                      : t('REVENDA.BRANDING.LOGIN.POSITION_RIGHT')
                }}
              </label>
            </div>
          </div>

          <div class="flex flex-col gap-2">
            <span class="text-sm text-n-slate-12">
              {{ t('REVENDA.BRANDING.LOGIN.FEATURES') }}
            </span>
            <LoginFeaturesEditor v-model="loginFeatures" />
          </div>
        </div>

        <div class="flex gap-3 justify-start">
          <Button
            :label="t('REVENDA.BRANDING.SAVE')"
            :is-loading="isSaving"
            @click="submit"
          />
          <Button
            variant="ghost"
            ruby
            :label="t('REVENDA.BRANDING.RESET')"
            :is-loading="isResetting"
            @click="onReset"
          />
        </div>
      </div>

      <aside class="w-full lg:w-80 lg:sticky lg:top-4">
        <BrandingPreview :branding="previewBranding" />
      </aside>
    </div>
  </div>
</template>
